import { buildFollowUpEnvelope } from '../../../a2ui/componentApis';

export const MAIL_TEMPLATES = new Set([
  'mail_connect', 'mail_digest', 'mail_draft', 'mail_confirm', 'mail_result', 'mail_unsubscribe_picker', 'mail_link',
]);

export function isMailTemplate(template) {
  return MAIL_TEMPLATES.has(template);
}

export const DIGEST_SECTIONS = [
  { bucket: 'needs_reply', label: 'Needs reply' },
  { bucket: 'action_required', label: 'Action required' },
  { bucket: 'waiting_on', label: 'Waiting on' },
];

export const OTHER_BUCKETS = [
  { bucket: 'fyi', singular: 'update', plural: 'updates' },
  { bucket: 'newsletter', singular: 'newsletter', plural: 'newsletters' },
  { bucket: 'promo', singular: 'promo', plural: 'promos' },
  { bucket: 'notification', singular: 'notification', plural: 'notifications' },
];

const OPERATION_LABELS = {
  archive: { verb: 'Archive', past: 'Archived' },
  unarchive: { verb: 'Move back to inbox', past: 'Moved back to inbox' },
  mark_read: { verb: 'Mark read', past: 'Marked read' },
  mark_unread: { verb: 'Mark unread', past: 'Marked unread' },
  star: { verb: 'Star', past: 'Starred' },
  unstar: { verb: 'Unstar', past: 'Unstarred' },
};

export function operationVerb(operation) {
  return OPERATION_LABELS[operation]?.verb || 'Update';
}

export function operationPast(operation) {
  return OPERATION_LABELS[operation]?.past || 'Updated';
}

export function capCount(n) {
  const v = Number(n) || 0;
  return v > 99 ? '99+' : String(Math.max(0, v));
}

export function emailsLabel(n) {
  return `${n} email${n === 1 ? '' : 's'}`;
}

export function isAllowedConnectUrl(url) {
  if (typeof url !== 'string' || !url) return false;
  let parsed;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  if (parsed.protocol !== 'https:') return false;
  const host = parsed.hostname.toLowerCase();
  return host === 'accounts.google.com'
    || host === 'login.microsoftonline.com'
    || host === 'login.live.com'
    || host.endsWith('.composio.dev');
}

export const MAIL_PROVIDERS = ['gmail', 'outlook'];

export function providerOf(value) {
  const v = typeof value === 'string' ? value.trim().toLowerCase() : '';
  return MAIL_PROVIDERS.includes(v) ? v : null;
}

export function providerList(value) {
  if (!Array.isArray(value)) return [];
  return [...new Set(value.map(providerOf).filter(Boolean))];
}

export function providerName(provider) {
  return provider === 'outlook' ? 'Outlook' : 'Gmail';
}

export function providerCompany(provider) {
  return provider === 'outlook' ? 'Microsoft' : 'Google';
}

export function parseSender(from) {
  const raw = typeof from === 'string' ? from.trim() : '';
  const m = raw.match(/^(.*?)\s*<([^>]*)>\s*$/);
  const address = (m ? m[2] : raw.includes('@') ? raw : '').trim();
  let name = (m ? m[1] : raw.includes('@') ? '' : raw).trim().replace(/^"(.*)"$/, '$1').trim();
  if (!name) name = address || 'Unknown sender';
  return { name, address };
}

export function monogram(name) {
  const ch = (name || '').trim().match(/[\p{L}\p{N}]/u);
  return ch ? ch[0].toUpperCase() : '?';
}

const MONOGRAM_COLORS = ['#5856D6', '#34C759', '#FF9500', '#FF2D55', '#007AFF', '#AF52DE', '#5AC8FA', '#A2845E'];

export function monogramColor(name) {
  let h = 0;
  for (const c of name || '') h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return MONOGRAM_COLORS[h % MONOGRAM_COLORS.length];
}

export function parseTime(value) {
  if (!value) return null;
  const t = typeof value === 'number' ? value : Date.parse(value);
  return Number.isFinite(t) ? t : null;
}

export function relativeTime(value, now) {
  const t = parseTime(value);
  if (t == null) return '';
  const diff = Math.max(0, now - t);
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day === 1) return 'Yesterday';
  if (day < 7) return `${day}d ago`;
  return new Date(t).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function absoluteTime(value) {
  const t = parseTime(value);
  if (t == null) return '';
  return new Date(t).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export function plural(n, singular, pluralForm = `${singular}s`) {
  return `${n} ${n === 1 ? singular : pluralForm}`;
}

export function resultText(data) {
  const count = Number(data?.count) || 0;
  let text;
  if (data?.action === 'undo_archive') {
    return `Moved ${emailsLabel(count)} back to your inbox`;
  }
  if (data?.action === 'send_draft') {
    const to = typeof data?.to === 'string' ? data.to : '';
    text = to ? `Sent to ${parseSender(to).name}` : 'Sent';
  } else if (data?.action === 'trash') {
    text = `Moved ${count} to Trash`;
  } else {
    text = `${operationPast(data?.operation)} ${emailsLabel(count)}`;
  }
  const failed = Array.isArray(data?.failed) ? data.failed.length : 0;
  if (data?.status === 'partial' || failed > 0) text += ` · ${failed} failed`;
  return text;
}

export function buildMailFollowUp(card, sourceComponentId, userMessage, context) {
  const surfaceId = card?.cardId || card?.id || undefined;
  return buildFollowUpEnvelope({ surfaceId, sourceComponentId, userMessage, context });
}

export function isAuthRequiredError(err) {
  const d = err?.detail;
  return err?.status === 409 && (d?.code === 'auth_required' || d === 'auth_required');
}

export function mailErrorKind(err) {
  const code = typeof err?.detail === 'object' && err.detail ? err.detail.code : err?.detail;
  if (isAuthRequiredError(err)) return 'auth';
  if (err?.status === 404 && code === 'feature_disabled') return 'disabled';
  if (err?.status === 404) return 'not_found';
  return 'error';
}

export function safeExternalUrl(url) {
  if (typeof url !== 'string' || !url) return null;
  let parsed;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return null;
  if (!parsed.hostname) return null;
  return { href: parsed.href, domain: parsed.hostname.replace(/^www\./, '') };
}

const connectedFollowUpSent = new Set();

export function claimConnectedFollowUp(cardId) {
  if (!cardId || connectedFollowUpSent.has(cardId)) return false;
  connectedFollowUpSent.add(cardId);
  return true;
}

export const CONNECT_STATES = new Set(['connected', 'not_connected', 'needs_more_access', 'disconnected']);

export function connectState(data) {
  if (data?.connected === true || data?.state === 'connected') return 'connected';
  if (CONNECT_STATES.has(data?.state)) return data.state;
  return data?.code === 'auth_required' ? 'disconnected' : 'not_connected';
}

export function mailLinkTarget(data) {
  const t = data?.target && typeof data.target === 'object' ? data.target : null;
  const widgetId = typeof t?.widget_id === 'string' ? t.widget_id.trim() : '';
  const pageFile = typeof t?.page_file === 'string' && /^[\w-]+\.html$/.test(t.page_file) ? t.page_file : 'index.html';
  if (!widgetId) return null;
  const raw = t.page_args && typeof t.page_args === 'object' && !Array.isArray(t.page_args) ? t.page_args : {};
  const pageArgs = Object.fromEntries(Object.entries(raw).filter(([, v]) => ['string', 'number', 'boolean'].includes(typeof v)));
  return { widgetId, pageFile, pageArgs };
}

export function mailLinkHref(target) {
  if (!target?.widgetId) return null;
  const params = new URLSearchParams({ widget: target.widgetId, data: 'user' });
  if (target.pageFile && target.pageFile !== 'index.html') {
    params.set('page_file', target.pageFile);
    if (Object.keys(target.pageArgs || {}).length) params.set('page_args', JSON.stringify(target.pageArgs));
  }
  return `/playground/widget-native/studio/standard?${params.toString()}`;
}
